/**
 * Stage 2 — relief. Land rises gently away from the sea, low-frequency fBm splits it into uplands
 * and basins, and explicit mountain *spines* (gently curving polylines random-walked across the
 * interior) raise ranges along their length. Ridged noise modulates each spine so it has peaks and
 * passes instead of a uniform wall; the spine's wide skirt becomes the hill belt that flanks it.
 * Water gets depth from its distance to land (lake surfaces are set by the lakes stage).
 */
import { HEX_W, NO_HEX } from '../core/hex';
import type { Rng } from '../core/rng';
import { Terrain } from '../core/types';
import { clamp, isLand, landHexes, type MapContext, smoothstep } from './context';
import { Noise, warp } from './noise';
import { bfs } from './paths';

/** Land elevation floor (sea level is 64). */
export const LAND_MIN = 66;
const LAND_MAX = 250;
/** Distance between spine samples, hex units. */
const SPINE_STEP = 0.5;
/** Skirt radius as a multiple of a spine's core half-width. */
const SKIRT = 2.6;

interface Spine {
  xs: number[];
  ys: number[];
  /** Cumulative length at each sample (for the along-range height profile). */
  cum: number[];
  peak: number;
  halfWidth: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function raiseElevation(ctx: MapContext, rng: Rng): void {
  const { grid, n, terrain, ux, uy, elevation } = ctx;
  const minDim = Math.min(ctx.extentX, ctx.extentY);
  const upland = new Noise(rng);
  const detail = new Noise(rng);
  const ridge = new Noise(rng);
  const warpX = new Noise(rng);
  const warpY = new Noise(rng);

  const seaHexes: number[] = [];
  const notSea = new Uint8Array(n);
  for (let h = 0; h < n; h++) {
    if (terrain[h] === Terrain.Ocean || terrain[h] === Terrain.Shallows) seaHexes.push(h);
    else notSea[h] = 1;
  }
  const seaDist = bfs(grid, seaHexes, notSea).dist;
  const land = landHexes(ctx);
  const spines = planSpines(ctx, rng, seaDist, land);

  const upFreq = 2.2 / minDim;
  for (const h of land) {
    const x = ux[h];
    const y = uy[h];
    const coastRise = 30 * smoothstep(0, 8, seaDist[h]);
    const up = 26 * upland.fbm(x * upFreq, y * upFreq, 4);
    // Warped sample position so spine flanks wiggle instead of running as perfect tubes.
    const [px, py] = warp(warpX, warpY, x, y, 0.12, 1.1);
    let mountain = 0;
    for (const s of spines) mountain = Math.max(mountain, spineHeight(s, px, py));
    const passes = 0.45 + 0.55 * ridge.ridged(x * 0.22, y * 0.22, 4);
    const e = 68 + coastRise + up + mountain * passes + 4 * detail.fbm(x * 0.3, y * 0.3, 3);
    elevation[h] = clamp(e, LAND_MIN, LAND_MAX);
  }

  // Sea floor deepens with distance from the coast.
  const water = new Uint8Array(n);
  for (let h = 0; h < n; h++) water[h] = isLand(terrain[h]) ? 0 : 1;
  const landDist = bfs(grid, land, water).dist;
  for (let h = 0; h < n; h++) {
    if (terrain[h] !== Terrain.Ocean && terrain[h] !== Terrain.Shallows) continue;
    const d = landDist[h] < 0 ? 12 : landDist[h];
    elevation[h] = clamp(61 - 6 * (d - 1) + 2 * detail.fbm(ux[h] * 0.3, uy[h] * 0.3, 2), 8, 62);
  }
}

/**
 * Chooses and random-walks the mountain spines. Count and length scale with the land area; each
 * new spine starts at the inland candidate farthest from the existing ones so ranges spread out.
 */
function planSpines(ctx: MapContext, rng: Rng, seaDist: Int32Array, land: number[]): Spine[] {
  const { ux, uy } = ctx;
  const count = Math.round(clamp(land.length / 650 + rng.range(-0.4, 0.8), 1, 6));
  let inland = land.filter((h) => seaDist[h] >= 3);
  if (inland.length === 0) inland = land;
  const spines: Spine[] = [];
  const size = Math.sqrt(land.length);
  for (let i = 0; i < count; i++) {
    let start = inland[0];
    let bestGap = -1;
    for (let c = 0; c < 12; c++) {
      const h = rng.pick(inland);
      let gap = Infinity;
      for (const s of spines) {
        for (let k = 0; k < s.xs.length; k++) {
          const dx = s.xs[k] - ux[h];
          const dy = s.ys[k] - uy[h];
          gap = Math.min(gap, dx * dx + dy * dy);
        }
      }
      if (gap > bestGap) {
        bestGap = gap;
        start = h;
      }
    }
    const length = rng.range(0.3, 0.55) * size;
    let dx = rng.gaussian();
    let dy = rng.gaussian();
    const norm = Math.sqrt(dx * dx + dy * dy) || 1;
    dx /= norm;
    dy /= norm;
    const bend = rng.range(-0.07, 0.07);
    const fwd = walkSpine(ctx, rng, ux[start], uy[start], dx, dy, bend, length / 2);
    const back = walkSpine(ctx, rng, ux[start], uy[start], -dx, -dy, -bend, length / 2);
    const xs = [...back.xs.slice(1).reverse(), ...fwd.xs];
    const ys = [...back.ys.slice(1).reverse(), ...fwd.ys];
    if (xs.length < 4) continue;
    const cum = [0];
    for (let k = 1; k < xs.length; k++) {
      const ex = xs[k] - xs[k - 1];
      const ey = ys[k] - ys[k - 1];
      cum.push(cum[k - 1] + Math.sqrt(ex * ex + ey * ey));
    }
    const halfWidth = rng.range(1.5, 2.4);
    const pad = SKIRT * halfWidth + 1.5;
    spines.push({
      xs,
      ys,
      cum,
      peak: rng.range(115, 160),
      halfWidth,
      minX: Math.min(...xs) - pad,
      maxX: Math.max(...xs) + pad,
      minY: Math.min(...ys) - pad,
      maxY: Math.max(...ys) + pad,
    });
  }
  return spines;
}

/**
 * Random walk with a constant bend (arcs like the Carpathians) plus jitter. Stops after the given
 * length or once it has been over the sea for a few steps — a range may end in a cape, not cross a gulf.
 */
function walkSpine(
  ctx: MapContext,
  rng: Rng,
  x0: number,
  y0: number,
  dx0: number,
  dy0: number,
  bend: number,
  length: number,
): { xs: number[]; ys: number[] } {
  const xs = [x0];
  const ys = [y0];
  let x = x0;
  let y = y0;
  let dx = dx0;
  let dy = dy0;
  let offshore = 0;
  const steps = Math.floor(length / SPINE_STEP);
  for (let s = 0; s < steps; s++) {
    const turn = bend + 0.06 * rng.gaussian();
    // Rotate by adding a perpendicular component and renormalising (no trig needed).
    const nx = dx - dy * turn;
    const ny = dy + dx * turn;
    const norm = Math.sqrt(nx * nx + ny * ny);
    dx = nx / norm;
    dy = ny / norm;
    x += dx * SPINE_STEP;
    y += dy * SPINE_STEP;
    const h = ctx.grid.fromWorld(x * HEX_W, y * HEX_W);
    if (h === NO_HEX) break;
    offshore = isLand(ctx.terrain[h]) ? 0 : offshore + 1;
    if (offshore >= 3) break;
    xs.push(x);
    ys.push(y);
  }
  return { xs, ys };
}

/** Height a spine contributes at (x, y): smooth core + wide skirt, tapering towards its ends. */
function spineHeight(s: Spine, x: number, y: number): number {
  if (x < s.minX || x > s.maxX || y < s.minY || y > s.maxY) return 0;
  let best = Infinity;
  let bestAt = 0;
  for (let k = 1; k < s.xs.length; k++) {
    const ax = s.xs[k - 1];
    const ay = s.ys[k - 1];
    const bx = s.xs[k] - ax;
    const by = s.ys[k] - ay;
    const len2 = bx * bx + by * by;
    let t = len2 > 0 ? ((x - ax) * bx + (y - ay) * by) / len2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const ex = x - (ax + bx * t);
    const ey = y - (ay + by * t);
    const d2 = ex * ex + ey * ey;
    if (d2 < best) {
      best = d2;
      bestAt = s.cum[k - 1] + t * (s.cum[k] - s.cum[k - 1]);
    }
  }
  const d = Math.sqrt(best);
  const skirtR = SKIRT * s.halfWidth;
  if (d >= skirtR) return 0;
  const t = bestAt / s.cum[s.cum.length - 1];
  const along = 0.35 + 0.65 * Math.sqrt(Math.max(0, 4 * t * (1 - t)));
  const u = d / s.halfWidth;
  const core = u < 1 ? (1 - u * u) * (1 - u * u) : 0;
  const v = d / skirtR;
  const skirt = (1 - v * v) * (1 - v * v);
  return s.peak * along * (0.75 * core + 0.25 * skirt);
}
